// ============================================================================
// FILE UPLOAD SERVICE - Three-step presigned URL upload to S3
// ----------------------------------------------------------------------------
// Flow:
//   1. Request presigned PUT URL from backend
//   2. Upload file directly to S3 (browser → S3, no backend bottleneck)
//   3. Confirm upload with backend (stores metadata in PostgreSQL)
//
// For retrieval: request signed GET URL from backend.
// Includes a thin URL cache with 58-minute TTL to avoid redundant requests.
//
// All uploads are tenant-isolated - the backend stamps tenant_id from JWT.
// ============================================================================

import { dataUrlToBlob } from '../utils/data-url-to-blob';
import { isRawMinioUrl, s3KeyFromMinioUrl } from '../utils/minio-url';
import { isBackendUploadForced } from '../utils/feature-flags';
import {
    CLOUD_MEDIA_KINDS,
    decideCloudMediaUpload,
    expectedCloudMediaKindForEntityType,
    isForbiddenRawCustomerMediaUpload,
    isUnclassifiedCustomerMediaUpload,
} from './cloud-media-policy';
import { devAutoLogin, getAccessToken, refreshAccessToken } from './data-service-api';
import { repairSingleMissingPdfEofByte } from './pdf-eof-repair';
import { serverMediaEntityId } from './server-media-entity-id';
import {
    NETWORK_UNREACHABLE_CODE,
    NETWORK_UNREACHABLE_MESSAGE,
    isNetworkFetchFailure,
    withNetworkRetry,
} from './network-resilience';
import {
    __resetFileReferenceHealthForTests,
    clearFileReferenceUnavailable,
    fileReferenceUnavailableReason,
    ingestProjectFileHealthManifest,
    markFileReferenceUnavailable,
} from './file-reference-health';

const API_BASE = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_BACKEND_URL) || '';

// ─── Signed URL Cache ────────────────────────────────────────────────────────
// Caches presigned GET URLs to avoid hitting the backend on every photo render.
// TTL: 58 minutes (presigned URLs expire at 60 minutes, 2-minute safety buffer).
const urlCache = new Map();
const CACHE_TTL_MS = 58 * 60 * 1000; // 58 minutes
const URL_CACHE_MAX_ENTRIES = 750;
const UPLOAD_URL_MIN_INTERVAL_MS = 200;
const UPLOAD_RATE_LIMIT_FALLBACK_MS = 15000;
const UPLOAD_COOLDOWN_LOG_INTERVAL_MS = 15000;
const UPLOAD_AUTH_COOLDOWN_MS = 30000;
const DIRECT_UPLOAD_FALLBACK_COOLDOWN_MS = 60000;
const DIRECT_UPLOAD_FALLBACK_MAX_BYTES = 25 * 1024 * 1024;
// `/api/media/normalize-image-and-store*` has two independent backend guards:
// 30 starts/minute at the route and one active ingest per tenant. Keep this
// tab below the route ceiling and absorb authoritative server throttling (the
// backend budget is shared with other tabs) without replaying the whole batch.
const TRUSTED_IMAGE_RATE_WINDOW_MS = 60 * 1000;
const TRUSTED_IMAGE_RATE_WINDOW_MAX = 28;
const TRUSTED_IMAGE_MAX_THROTTLE_RETRIES = 2;
const TRUSTED_IMAGE_RETRY_JITTER_MAX_MS = 1000;
// UF-CP-4 (2026-05-12): files over this threshold on the backend-fallback
// path use the streaming /upload-stream endpoint (multipart/form-data piped
// straight into MinIO). Below the threshold, the legacy base64-over-JSON
// /direct-upload path is kept — it's well-tested + has the Fix A durability
// gate from v5.19.60. 20 MB sits below the base64 ceiling (25 MB raw → ~33 MB
// base64) so even with the inflation tax, sub-threshold uploads always fit.
const STREAMING_UPLOAD_THRESHOLD_BYTES = 20 * 1024 * 1024;

let uploadUrlGate = Promise.resolve();
let lastUploadUrlRequestAt = 0;
let uploadUrlCooldownUntil = 0;
let lastUploadCooldownLogAt = 0;
let uploadAuthCooldownUntil = 0;
let uploadAuthCooldownToken = null;
let directS3UploadPausedUntil = 0;
let lastDirectS3FallbackLogAt = 0;
let trustedImageUploadGate = Promise.resolve();
let trustedImageRetryNotBefore = 0;
let lastTrustedImageCooldownLogAt = 0;
const trustedImageRequestStarts = [];
// Metadata alone is not a safe single-flight key: a manual markup replacement
// can reuse the same entity id, filename, size, and derivative attestation while
// carrying different bytes. Keep a second-level payload key so only the exact
// same Blob/ArrayBuffer object (or equal immutable data-URL string) can join an
// existing physical request.
const inFlightUploads = new Map();
const projectReferenceReconciliations = new Map();
const projectReferenceLastStartedAt = new Map();
const projectReferenceGlobalStarts = [];
const PROJECT_REFERENCE_RECONCILE_COOLDOWN_MS = 5 * 60 * 1000;
const PROJECT_REFERENCE_FORCE_MIN_INTERVAL_MS = 12 * 1000;
const PROJECT_REFERENCE_GLOBAL_WINDOW_MS = 60 * 1000;
const PROJECT_REFERENCE_GLOBAL_MAX = 4;
const PROJECT_REFERENCE_COOLDOWN_MAX_ENTRIES = 500;

function pruneUrlCache(nowMs = Date.now()) {
    for (const [key, entry] of urlCache) {
        if (!entry || !Number.isFinite(entry.expiresAt) || entry.expiresAt <= nowMs) {
            urlCache.delete(key);
        }
    }
    while (urlCache.size > URL_CACHE_MAX_ENTRIES) {
        const oldestKey = urlCache.keys().next().value;
        if (oldestKey === undefined) break;
        urlCache.delete(oldestKey);
    }
}

function pruneProjectReferenceCooldowns(nowMs) {
    for (const [projectId, startedAt] of projectReferenceLastStartedAt) {
        if (!Number.isFinite(startedAt)
            || nowMs - startedAt >= PROJECT_REFERENCE_RECONCILE_COOLDOWN_MS) {
            projectReferenceLastStartedAt.delete(projectId);
        }
    }
    while (projectReferenceLastStartedAt.size > PROJECT_REFERENCE_COOLDOWN_MAX_ENTRIES) {
        const oldestProjectId = projectReferenceLastStartedAt.keys().next().value;
        if (oldestProjectId === undefined) break;
        projectReferenceLastStartedAt.delete(oldestProjectId);
    }
}

function getCachedUrl(s3Key) {
    const cached = urlCache.get(s3Key);
    if (cached && cached.expiresAt > Date.now()) {
        // Refresh insertion order without extending the signed URL's lifetime.
        // This makes the bounded Map an LRU cache instead of evicting a hot key.
        urlCache.delete(s3Key);
        urlCache.set(s3Key, cached);
        return cached.url;
    }
    urlCache.delete(s3Key);
    return null;
}

function setCachedUrl(s3Key, url) {
    const nowMs = Date.now();
    pruneUrlCache(nowMs);
    urlCache.delete(s3Key);
    urlCache.set(s3Key, { url, expiresAt: nowMs + CACHE_TTL_MS });
    pruneUrlCache(nowMs);
}

function makeUploadAbortError(signal) {
    const reason = signal?.reason;
    const message = typeof reason === 'string' && reason.trim()
        ? `File upload aborted: ${reason.trim()}`
        : 'File upload aborted because the owning project operation was superseded.';
    const error = makeUploadError(message, {
        code: 'file_upload_aborted',
        retryable: true,
    });
    error.name = 'AbortError';
    return error;
}

function throwIfUploadAborted(signal) {
    if (signal?.aborted) throw makeUploadAbortError(signal);
}

function sleep(ms, signal = null) {
    if (!signal) return new Promise(resolve => setTimeout(resolve, ms));
    throwIfUploadAborted(signal);
    return new Promise((resolve, reject) => {
        let settled = false;
        const finish = (callback) => {
            if (settled) return;
            settled = true;
            signal.removeEventListener('abort', onAbort);
            callback();
        };
        const timeoutId = setTimeout(() => finish(resolve), ms);
        const onAbort = () => {
            clearTimeout(timeoutId);
            finish(() => reject(makeUploadAbortError(signal)));
        };
        signal.addEventListener('abort', onAbort, { once: true });
    });
}

function normaliseRetryAfterMs(value) {
    if (value == null || value === '') return null;
    const raw = String(value).trim();
    const seconds = Number(raw);
    if (Number.isFinite(seconds) && seconds >= 0) {
        return Math.max(0, Math.ceil(seconds * 1000));
    }
    const dateMs = Date.parse(raw);
    if (Number.isFinite(dateMs)) {
        return Math.max(0, dateMs - Date.now());
    }
    return null;
}

function retryAfterMsFromBody(body) {
    if (!body || typeof body !== 'object') return null;
    for (const key of ['retryAfterMs', 'retry_after_ms']) {
        const ms = Number(body[key]);
        if (Number.isFinite(ms) && ms >= 0) return Math.ceil(ms);
    }
    for (const key of ['retryAfterSeconds', 'retry_after_seconds', 'retryAfter', 'retry_after']) {
        const ms = normaliseRetryAfterMs(body[key]);
        if (ms != null) return ms;
    }
    const text = String(body.message || body.error || '');
    const match = text.match(/retry\s+in\s+(\d+)\s+seconds?/i);
    return match ? Number(match[1]) * 1000 : null;
}

function retryAfterMsFromResponse(response, body) {
    const headerMs = normaliseRetryAfterMs(response?.headers?.get?.('retry-after'));
    if (headerMs != null) return headerMs;
    return retryAfterMsFromBody(body);
}

// Shared by the legacy streaming/direct/presign/confirm 429 paths. `0` is an
// authoritative value, not a missing one, so fallback must be nullish-safe.
export function resolveUploadRetryAfterMs(response, body) {
    return retryAfterMsFromResponse(response, body) ?? UPLOAD_RATE_LIMIT_FALLBACK_MS;
}

function makeUploadError(message, props = {}) {
    return Object.assign(new Error(message), props);
}

function normaliseAccessToken(token) {
    if (typeof token !== 'string') return null;
    const trimmed = token.trim();
    if (!trimmed || /^(null|undefined)$/i.test(trimmed)) return null;
    return trimmed;
}

function readAccessToken() {
    try {
        return normaliseAccessToken(getAccessToken?.());
    } catch {
        try {
            return typeof sessionStorage !== 'undefined'
                ? normaliseAccessToken(sessionStorage.getItem('qm_access_token'))
                : null;
        } catch {
            return null;
        }
    }
}

function isJwtExpiredOrInvalid(token) {
    const safeToken = normaliseAccessToken(token);
    if (!safeToken) return true;
    try {
        const parts = safeToken.split('.');
        if (parts.length !== 3) return true;
        const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
        const decoded = JSON.parse(atob(padded));
        if (typeof decoded.exp !== 'number') return true;
        const nowSec = Math.floor(Date.now() / 1000);
        return decoded.exp < nowSec + 30;
    } catch {
        return true;
    }
}

function makeAuthUnavailableError(message = 'File upload requires a valid QuoteMaker login session.') {
    return makeUploadError(message, {
        status: 401,
        code: 'file_upload_auth_unavailable',
        authUnavailable: true,
    });
}

export function isFileUploadAuthError(err) {
    return !!err && (
        err.authUnavailable === true
        || err.code === 'file_upload_auth_unavailable'
        || err.status === 401
        || err.status === 403
    );
}

function authCooldownApplies(token) {
    if (Date.now() >= uploadAuthCooldownUntil) return false;
    const safeToken = normaliseAccessToken(token);
    return uploadAuthCooldownToken === (safeToken || null);
}

function setUploadAuthCooldown(token) {
    uploadAuthCooldownToken = normaliseAccessToken(token);
    uploadAuthCooldownUntil = Date.now() + UPLOAD_AUTH_COOLDOWN_MS;
}

function setUploadCooldown(ms) {
    const parsedMs = Number(ms);
    const requestedMs = Number.isFinite(parsedMs) && parsedMs >= 0
        ? parsedMs
        : UPLOAD_RATE_LIMIT_FALLBACK_MS;
    // Keep a one-second client pacing floor, but do not reinterpret an
    // authoritative zero as the missing-value 15-second fallback.
    const safeMs = Math.max(1000, requestedMs);
    uploadUrlCooldownUntil = Math.max(uploadUrlCooldownUntil, Date.now() + safeMs);
}

function directS3UploadPaused() {
    return Date.now() < directS3UploadPausedUntil;
}

// Council #4 fix (v5.19.60): operator opt-out for direct-PUT. When the
// localStorage flag is truthy, every upload routes through the backend
// (eliminating the always-failing direct-PUT attempt on networks where
// MinIO host port is unreachable). Read defensively — non-browser
// environments + privacy modes that throw on localStorage access return
// false (= direct-PUT-first behaviour preserved). Cached per call so
// rapid sequential uploads don't re-probe localStorage.
// Default ON since 2026-06-05 (v5.41.51): route every upload through the
// authenticated backend instead of a direct browser→MinIO presigned PUT. The
// direct PUT targets the host-published MinIO port (:9010), unreachable from any
// device that isn't the Docker host, so it errored ERR_CONNECTION_REFUSED on
// Customer Zero's second device. Backend-routed uploads always work (the backend
// reaches MinIO over the Docker-internal network). Opt back into the direct-PUT
// fast path with `qm_force_backend_upload=false` on a LAN where the browser can
// reach :9010. Single source of truth: feature-flags.js / isBackendUploadForced.
function _forceBackendUploadFlag() {
    return isBackendUploadForced();
}

function markDirectS3UploadUnavailable(reason) {
    directS3UploadPausedUntil = Math.max(directS3UploadPausedUntil, Date.now() + DIRECT_UPLOAD_FALLBACK_COOLDOWN_MS);
    const now = Date.now();
    if (now - lastDirectS3FallbackLogAt < UPLOAD_COOLDOWN_LOG_INTERVAL_MS) return;
    lastDirectS3FallbackLogAt = now;
    const suffix = reason ? ` (${reason})` : '';
    console.info(`[FileUpload] Direct MinIO upload unavailable${suffix}; using backend upload fallback for local saves.`);
}

export function getFileUploadCooldownMs() {
    return Math.max(0, uploadUrlCooldownUntil - Date.now());
}

export function getFileUploadAuthCooldownMs() {
    const token = readAccessToken();
    if (!authCooldownApplies(token)) return 0;
    return Math.max(0, uploadAuthCooldownUntil - Date.now());
}

export function isFileUploadDeferralError(err) {
    return !!err && (err.deferred === true || err.code === 'file_upload_rate_limited');
}

function makeCooldownError(ms = getFileUploadCooldownMs()) {
    const seconds = Math.max(1, Math.ceil(ms / 1000));
    return makeUploadError(`File upload temporarily rate limited; retry in ${seconds} seconds`, {
        status: 429,
        code: 'file_upload_rate_limited',
        retryAfterMs: ms,
        retryAfterSeconds: seconds,
        deferred: true,
    });
}

function logUploadCooldownOnce(ms) {
    const now = Date.now();
    if (now - lastUploadCooldownLogAt < UPLOAD_COOLDOWN_LOG_INTERVAL_MS) return;
    lastUploadCooldownLogAt = now;
    console.warn(`[FileUpload] Upload URL requests are cooling down for ${Math.ceil(ms / 1000)}s after backend rate limiting.`);
}

function logTrustedImageCooldownOnce(ms) {
    const now = Date.now();
    if (now - lastTrustedImageCooldownLogAt < UPLOAD_COOLDOWN_LOG_INTERVAL_MS) return;
    lastTrustedImageCooldownLogAt = now;
    console.warn(`[FileUpload] Trusted image normalization is cooling down for ${Math.ceil(ms / 1000)}s after backend throttling.`);
}

function trustedImageRetryDelayMs(retryAfterMs) {
    const parsedRetryAfterMs = Number(retryAfterMs);
    const requestedDelayMs = Number.isFinite(parsedRetryAfterMs) && parsedRetryAfterMs >= 0
        ? parsedRetryAfterMs
        : UPLOAD_RATE_LIMIT_FALLBACK_MS;
    // Even an authoritative Retry-After: 0 gets a small local pacing floor,
    // but it must not be mistaken for a missing header and inflated to 15s.
    const baseMs = Math.max(250, requestedDelayMs);
    const jitterRange = Math.min(
        TRUSTED_IMAGE_RETRY_JITTER_MAX_MS,
        Math.max(100, Math.ceil(baseMs * 0.1)),
    );
    return baseMs + Math.floor(Math.random() * (jitterRange + 1));
}

function deferTrustedImageRequests(retryAfterMs) {
    const delayMs = trustedImageRetryDelayMs(retryAfterMs);
    trustedImageRetryNotBefore = Math.max(trustedImageRetryNotBefore, Date.now() + delayMs);
    logTrustedImageCooldownOnce(delayMs);
    return delayMs;
}

async function waitForTrustedImageRateBudget(signal) {
    for (;;) {
        throwIfUploadAborted(signal);
        const now = Date.now();
        while (trustedImageRequestStarts.length > 0
            && trustedImageRequestStarts[0] <= now - TRUSTED_IMAGE_RATE_WINDOW_MS) {
            trustedImageRequestStarts.shift();
        }
        const windowDelay = trustedImageRequestStarts.length >= TRUSTED_IMAGE_RATE_WINDOW_MAX
            ? trustedImageRequestStarts[0] + TRUSTED_IMAGE_RATE_WINDOW_MS - now
            : 0;
        const retryDelay = trustedImageRetryNotBefore - now;
        const delayMs = Math.max(0, windowDelay, retryDelay);
        if (delayMs > 0) {
            await sleep(delayMs, signal);
            continue;
        }
        trustedImageRequestStarts.push(Date.now());
        return;
    }
}

function awaitWithAbort(promise, signal) {
    if (!signal) return promise;
    if (signal.aborted) return Promise.reject(makeUploadAbortError(signal));
    return new Promise((resolve, reject) => {
        let settled = false;
        const finish = (callback) => {
            if (settled) return;
            settled = true;
            signal.removeEventListener('abort', onAbort);
            callback();
        };
        const onAbort = () => finish(() => reject(makeUploadAbortError(signal)));
        signal.addEventListener('abort', onAbort, { once: true });
        promise.then(
            value => finish(() => resolve(value)),
            error => finish(() => reject(error)),
        );
    });
}

function joinInFlightUpload(entry, signal) {
    throwIfUploadAborted(signal);
    entry.waiterCount += 1;
    let released = false;
    const releaseWaiter = () => {
        if (released) return;
        released = true;
        entry.waiterCount = Math.max(0, entry.waiterCount - 1);
        if (entry.waiterCount === 0 && !entry.settled) {
            // No caller still owns this physical request. Remove it before
            // aborting so a new caller can start fresh instead of joining a
            // doomed promise; the identity check in the request's finally
            // prevents the old request from deleting that replacement.
            deleteInFlightUpload(entry);
            entry.controller.abort('all upload callers were superseded');
        }
    };
    // Release ownership synchronously on abort. This matters when every owner
    // is superseded and a fresh caller for the same key arrives in the same
    // event-loop turn: it must not join the already-doomed request.
    signal?.addEventListener('abort', releaseWaiter, { once: true });
    if (signal?.aborted) releaseWaiter();
    return awaitWithAbort(entry.promise, signal).finally(() => {
        signal?.removeEventListener('abort', releaseWaiter);
        releaseWaiter();
    });
}

function getInFlightUpload(key, payload) {
    return inFlightUploads.get(key)?.get(payload);
}

function setInFlightUpload(entry) {
    let payloads = inFlightUploads.get(entry.key);
    if (!payloads) {
        payloads = new Map();
        inFlightUploads.set(entry.key, payloads);
    }
    payloads.set(entry.payload, entry);
}

function deleteInFlightUpload(entry) {
    const payloads = inFlightUploads.get(entry.key);
    if (payloads?.get(entry.payload) !== entry) return;
    payloads.delete(entry.payload);
    if (payloads.size === 0) inFlightUploads.delete(entry.key);
}

function scheduleTrustedImageUpload(task, signal) {
    const prior = trustedImageUploadGate.catch(() => {});
    const execution = prior.then(async () => {
        throwIfUploadAborted(signal);
        return task();
    });
    // The internal tail retains serialization even when the outward promise is
    // rejected immediately by an abort while it is still queued.
    trustedImageUploadGate = execution.catch(() => {});
    return awaitWithAbort(execution, signal);
}

async function waitForUploadUrlBudget() {
    const turn = uploadUrlGate.catch(() => {}).then(async () => {
        const cooldownMs = getFileUploadCooldownMs();
        if (cooldownMs > 0) {
            logUploadCooldownOnce(cooldownMs);
            throw makeCooldownError(cooldownMs);
        }

        const elapsed = Date.now() - lastUploadUrlRequestAt;
        if (elapsed < UPLOAD_URL_MIN_INTERVAL_MS) {
            await sleep(UPLOAD_URL_MIN_INTERVAL_MS - elapsed);
        }
        lastUploadUrlRequestAt = Date.now();
    });
    uploadUrlGate = turn.catch(() => {});
    return turn;
}

async function parseJsonSafe(response) {
    try {
        return await response.json();
    } catch {
        return {};
    }
}

// ─── Network-only retry for the upload lanes ────────────────────────────────
// Field report 2026-09-22 (Android / Brave / 4G): "Upload failed:
// <photo>.jpg - Failed to fetch". The trusted-image POST, the presign, the
// PUT, the backend direct-upload and the confirm were all bare fetches, so
// one dropped packet failed the photo outright. Each leg now runs under
// withNetworkRetry with a NETWORK-ONLY predicate: a fetch TypeError, a
// per-attempt timeout, or a 502/503/504 from the gateway in front of the
// backend. 4xx, 429 and 500 keep their existing handling untouched.
const UPLOAD_NETWORK_RETRY_ATTEMPTS = 3;
// Presign and confirm carry a few hundred bytes of JSON.
const UPLOAD_CONTROL_TIMEOUT_MS = 30 * 1000;
// The trusted-image POST carries the compressed derivative (hundreds of KB,
// never the camera original). Two minutes with no completion on 4G is a dead
// socket, and the retry re-sends from a fresh FormData.
const TRUSTED_IMAGE_POST_TIMEOUT_MS = 120 * 1000;
// The direct PUT and the base64 direct-upload carry whole files (documents,
// video frames, up to the 25 MB fallback ceiling). Never timed: a slow link is
// not a dead one, and a false timeout would re-send the whole payload.
const UPLOAD_BYTES_TIMEOUT_MS = 0;

function isGatewayUnavailableStatus(status) {
    return status === 502 || status === 503 || status === 504;
}

// A Response whose body has already been read on the retry path. Exposes the
// members the upload lanes consult (ok / status / headers / json) with the
// parsed body replayed, so downstream `parseJsonSafe` still sees it.
function withParsedBody(response, body) {
    return {
        ok: response.ok,
        status: response.status,
        headers: response.headers,
        json: async () => body,
        text: async () => JSON.stringify(body),
    };
}

/**
 * Send one upload-lane request with the network-only retry.
 * `send(signal)` issues the fetch; the signal carries the caller's abort and
 * the per-attempt timeout. A 502/503/504 answer is thrown so the retry loop
 * treats it like a dropped connection (unless `passThrough503(body)` claims
 * that 503 - the trusted-image lane keeps its IMAGE_INGEST_SATURATED
 * Retry-After handling). Every other Response is returned untouched for the
 * lane's existing 401/403/429/4xx handling. After the last attempt a dropped
 * connection surfaces as the normalised NETWORK_UNREACHABLE upload error
 * rather than a bare `Failed to fetch`.
 */
async function sendWithNetworkRetry(send, { label, timeoutMs = 0, signal, passThrough503, shouldRetry } = {}) {
    try {
        return await withNetworkRetry(async (attemptSignal) => {
            const response = await send(attemptSignal);
            const status = response?.status;
            if (!isGatewayUnavailableStatus(status)) return response;
            const body = await parseJsonSafe(response);
            if (status === 503 && typeof passThrough503 === 'function' && passThrough503(body)) {
                return withParsedBody(response, body);
            }
            throw makeUploadError(body.error || body.message || `${label} failed (${status})`, {
                status,
                code: body.code,
                body,
                transient: true,
            });
        }, {
            attempts: UPLOAD_NETWORK_RETRY_ATTEMPTS,
            timeoutMs,
            signal,
            ...(typeof shouldRetry === 'function' ? { shouldRetry } : {}),
        });
    } catch (err) {
        if (isNetworkFetchFailure(err) || err?.name === 'TimeoutError') {
            throw makeUploadError(NETWORK_UNREACHABLE_MESSAGE, {
                status: 0,
                code: NETWORK_UNREACHABLE_CODE,
                networkFailure: true,
                retryable: true,
                cause: err,
            });
        }
        throw err;
    }
}

async function blobToBase64(blob) {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
}

function uploadRequestKey({ projectId, intakeJobId, intakeLane, entityType, entityId, contentType, originalFilename, cloudMedia }) {
    return [
        projectId || '',
        intakeJobId || '',
        intakeLane || 'visit',
        entityType || '',
        entityId || '',
        contentType || '',
        originalFilename || '',
        cloudMedia ? JSON.stringify(cloudMedia) : '',
    ].join('|');
}

const SERVER_NORMALISED_IMAGE_LANES = new Set([
    'project_photo',
    'project_video_frame',
    'intake_visit_photo_preview',
]);

function multipartAuthHeaders(token) {
    const headers = {};
    const safeToken = normaliseAccessToken(token);
    if (safeToken) headers.Authorization = `Bearer ${safeToken}`;
    return headers;
}

async function uploadTrustedImageDerivative({
    blob,
    projectId,
    intakeJobId,
    /** migration 114: 'visit' (default) or 'reference'. */
    intakeLane = 'visit',
    entityType,
    entityId,
    originalFilename,
    authToken,
    onProgress,
    signal,
}) {
    const intakeParent = entityType === 'intake_visit_photo_preview';
    if (intakeParent && !intakeJobId) {
        throw makeUploadError('intakeJobId is required for an intake photo derivative', {
            status: 400,
            code: 'intake_job_id_required',
        });
    }
    if (!intakeParent && !projectId) {
        throw makeUploadError('projectId is required for a project image derivative', {
            status: 400,
            code: 'project_id_required',
        });
    }
    const storedEntityId = serverMediaEntityId(entityId);
    const path = intakeParent
        ? `/api/media/normalize-image-and-store-intake?jobId=${encodeURIComponent(intakeJobId)}&entityId=${encodeURIComponent(storedEntityId)}&intakeLane=${encodeURIComponent(intakeLane)}`
        : `/api/media/normalize-image-and-store?projectId=${encodeURIComponent(projectId)}&entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(storedEntityId)}`;
    // One attempt builds one FormData: a retried attempt after a dropped
    // connection must re-send from fresh bytes, not a consumed body.
    const requestImageNormalization = (token) => sendWithNetworkRetry(
        (attemptSignal) => {
            const form = new FormData();
            form.append('file', blob, originalFilename || 'image-derivative');
            return fetch(`${API_BASE}${path}`, {
                method: 'POST',
                headers: multipartAuthHeaders(token),
                body: form,
                signal: attemptSignal,
            });
        },
        {
            label: 'Server image normalization',
            timeoutMs: TRUSTED_IMAGE_POST_TIMEOUT_MS,
            signal,
            // The backend's own "one ingest per tenant" 503 carries a
            // Retry-After; the throttle branch below honours it.
            passThrough503: (body) => body?.code === 'IMAGE_INGEST_SATURATED',
        },
    );
    return scheduleTrustedImageUpload(async () => {
        onProgress?.(20);
        let authRefreshAttempted = false;
        let throttleRetries = 0;
        for (;;) {
            throwIfUploadAborted(signal);
            await waitForTrustedImageRateBudget(signal);
            const response = await requestImageNormalization(authToken);
            if (response.ok) {
                const body = await response.json();
                if (!body?.id || !body?.s3_key || !body?.cloudMedia) {
                    throw makeUploadError('Server image normalization returned an incomplete durable receipt', {
                        status: 502,
                        code: 'trusted_image_receipt_incomplete',
                    });
                }
                onProgress?.(100);
                return {
                    s3Key: body.s3_key,
                    size: body.size_bytes,
                    fileMetadataId: body.id,
                    contentType: body.content_type,
                    cloudMedia: body.cloudMedia,
                };
            }

            const body = await parseJsonSafe(response);
            const message = body.error || body.message || `Server image normalization failed (${response.status})`;
            // Only a 401 is a credential problem a fresh token can answer. A
            // 403 on this route is policy (LIVE_WRITE_ROLE_FORBIDDEN /
            // LIVE_WRITE_AREA_FORBIDDEN / LIVE_WRITE_ACCOUNT_INACTIVE in
            // live-write-authorization.js) and used to spend the single-use
            // refresh token for nothing - on a flaky link that rotation was one
            // more chance to lose the reply and strand the session.
            if (response.status === 401 && !authRefreshAttempted) {
                authRefreshAttempted = true;
                const freshToken = await getFreshUploadAuthToken(authToken);
                if (freshToken) {
                    authToken = freshToken;
                    continue;
                }
            }
            if (response.status === 401) {
                markUploadAuthFailure(authToken);
                throw makeAuthUnavailableError(message);
            }
            if (response.status === 403) {
                throw makeUploadError(message, {
                    status: 403,
                    code: body.code || 'file_upload_forbidden',
                    body,
                });
            }

            const transientThrottle = response.status === 429
                || (response.status === 503 && body.code === 'IMAGE_INGEST_SATURATED');
            if (transientThrottle) {
                const retryAfterMs = retryAfterMsFromResponse(response, body) ?? UPLOAD_RATE_LIMIT_FALLBACK_MS;
                const retryDelayMs = deferTrustedImageRequests(retryAfterMs);
                if (throttleRetries < TRUSTED_IMAGE_MAX_THROTTLE_RETRIES) {
                    throttleRetries += 1;
                    continue;
                }
                throw makeCooldownError(retryDelayMs);
            }

            // Deterministic policy/identity/parent 4xx responses are never
            // retried. The caller must fix the request rather than amplify it.
            throw makeUploadError(message, { status: response.status, code: body.code, body });
        }
    }, signal);
}

function makeCloudMediaPolicyError(reason) {
    const err = new Error(`Customer media upload blocked: ${reason}`);
    err.code = reason;
    err.status = 400;
    return err;
}

function assertCloudMediaUploadAllowed({ entityType, contentType, cloudMedia, blob }) {
    if (isForbiddenRawCustomerMediaUpload(entityType, contentType)) {
        throw makeCloudMediaPolicyError('raw_original_cloud_upload_forbidden');
    }
    const expectedKind = expectedCloudMediaKindForEntityType(entityType);
    if (!expectedKind) {
        if (isUnclassifiedCustomerMediaUpload(entityType, contentType)) {
            throw makeCloudMediaPolicyError('unclassified_customer_media_lane_forbidden');
        }
        return;
    }
    const decision = decideCloudMediaUpload(cloudMedia, expectedKind);
    if (!decision.allowed) throw makeCloudMediaPolicyError(decision.reason);
    if (cloudMedia.derivativeContentType !== String(contentType || '').trim().toLowerCase()) {
        throw makeCloudMediaPolicyError('derivative_content_type_mismatch');
    }
    if (cloudMedia.derivativeBytes !== blob.size) {
        throw makeCloudMediaPolicyError('derivative_size_mismatch');
    }
}

// ─── Auth Helper ────────────────────────────────────────────────────────────
async function ensureUploadAuthSession() {
    try {
        await devAutoLogin();
    } catch {
        // Auth helpers are best-effort. The explicit token checks below keep
        // uploads quiet if login/refresh cannot complete.
    }

    const token = readAccessToken();
    if (authCooldownApplies(token)) {
        throw makeAuthUnavailableError('File upload is paused until the QuoteMaker login session is refreshed.');
    }
    if (!token) {
        setUploadAuthCooldown(null);
        throw makeAuthUnavailableError('File upload skipped because the current session is not authenticated.');
    }
    if (isJwtExpiredOrInvalid(token)) {
        setUploadAuthCooldown(token);
        throw makeAuthUnavailableError('File upload skipped because the current login token is expired.');
    }
    return token;
}

function markUploadAuthFailure(token) {
    setUploadAuthCooldown(token);
}

async function getFreshUploadAuthToken(previousToken) {
    try {
        const refreshed = normaliseAccessToken(await refreshAccessToken());
        if (refreshed && refreshed !== normaliseAccessToken(previousToken) && !isJwtExpiredOrInvalid(refreshed)) {
            return refreshed;
        }
    } catch {
        // No refresh token, expired refresh token, or backend unavailable.
    }

    try {
        await devAutoLogin();
    } catch {
        // Best-effort only.
    }

    const token = readAccessToken();
    if (token && token !== normaliseAccessToken(previousToken) && !isJwtExpiredOrInvalid(token)) {
        return token;
    }
    return null;
}

// Only a 401 earns a token rotation. A 403 on the file routes is policy
// (live-write-authorization.js), which a fresh token cannot change, and
// spending the single-use refresh token on it was one more rotation to lose
// on a bad link.
async function retryRequestWithFreshAuth(response, authToken, requestFn) {
    if (response.status !== 401) {
        return { response, authToken };
    }

    const freshToken = await getFreshUploadAuthToken(authToken);
    if (!freshToken) {
        return { response, authToken };
    }

    return {
        response: await requestFn(freshToken),
        authToken: freshToken,
    };
}

// UF-CP-4 (2026-05-12): streaming-upload sibling of the legacy base64 path.
// Uses XMLHttpRequest (not fetch) so we get an `upload.onprogress` event that
// the UI can wire to a real progress bar — Customer Zero notices when a 30 MB
// drone clip stalls silently. The endpoint is POST /api/files/upload-stream
// with `?s3Key=<key>` in the query and a multipart/form-data body whose
// single `file` field is the blob. Auth via Authorization: Bearer header.
//
// Why XHR and not fetch + ReadableStream body: streaming request bodies in
// fetch are Chrome 105+ and Edge only — Firefox still emits a full buffer
// even with `duplex: 'half'`. XHR is universally supported, the progress
// event fires throughout the upload, and the multipart consumer on the
// backend reads from a Node Readable regardless of how the client framed it.
function uploadBlobViaStreamingEndpoint({
    blob,
    s3Key,
    contentType,
    authToken,
    onProgress,
}) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const url = `${API_BASE}/api/files/upload-stream?s3Key=${encodeURIComponent(s3Key)}`;
        xhr.open('POST', url, true);
        const safeToken = normaliseAccessToken(authToken);
        if (safeToken) {
            xhr.setRequestHeader('Authorization', `Bearer ${safeToken}`);
        }
        // Do NOT set Content-Type — the browser fills in
        // multipart/form-data; boundary=... automatically when we send a
        // FormData, and overriding it strips the boundary parameter (which
        // fastify/multipart needs to parse the body).

        if (onProgress && xhr.upload) {
            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    // Scale 0–100% across the upload phase. The legacy path
                    // emits 55/70/100 progress checkpoints; this is finer.
                    const pct = Math.min(99, Math.round((event.loaded / event.total) * 99));
                    onProgress(pct);
                }
            };
        }

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                let body = null;
                try { body = JSON.parse(xhr.responseText); } catch {
                    // Body not JSON — fall back to minimal shape.
                }
                if (onProgress) onProgress(100);
                resolve({
                    s3Key,
                    size: blob.size,
                    fileMetadataId: body?.id || null,
                    contentType: body?.content_type || contentType,
                });
                return;
            }
            // Error path. Best-effort parse for { error, code }.
            let parsed = null;
            try { parsed = JSON.parse(xhr.responseText); } catch { /* ignore */ }
            const message = parsed?.error
                || parsed?.message
                || `Streaming upload failed (${xhr.status})`;
            if (xhr.status === 401 || xhr.status === 403) {
                markUploadAuthFailure(authToken);
                reject(makeAuthUnavailableError(message));
                return;
            }
            if (xhr.status === 429) {
                const cooldownMs = resolveUploadRetryAfterMs(null, parsed);
                setUploadCooldown(cooldownMs);
                logUploadCooldownOnce(cooldownMs);
                reject(makeCooldownError(cooldownMs));
                return;
            }
            reject(makeUploadError(message, {
                status: xhr.status,
                code: parsed?.code,
                body: parsed,
            }));
        };
        xhr.onerror = () => {
            reject(makeUploadError('Streaming upload network error', {
                status: 0,
                code: 'file_upload_streaming_network_error',
            }));
        };
        xhr.onabort = () => {
            reject(makeUploadError('Streaming upload aborted', {
                status: 0,
                code: 'file_upload_streaming_aborted',
            }));
        };

        const form = new FormData();
        // Field name matches the fastify/multipart `request.file()` contract —
        // it returns the first file part regardless of field name, but using
        // `file` here keeps parity with the gold-standards upload route.
        form.append('file', blob, 'upload.bin');
        xhr.send(form);
    });
}

async function uploadBlobViaBackendFallback({
    blob,
    s3Key,
    contentType,
    authToken,
    onProgress,
}) {
    // UF-CP-4 (2026-05-12): dispatch on size. Files >20 MB stream through the
    // multipart endpoint (no base64 inflation, no per-request memory ceiling).
    // Below the threshold, the legacy base64-over-JSON path stays in use — it's
    // already covered by Fix A (v5.19.60) post-write durability verification
    // and has years of production miles. Larger files always use the streaming
    // path; browser-local switch state cannot demote them to the 25 MiB legacy
    // JSON lane.
    if (blob.size > STREAMING_UPLOAD_THRESHOLD_BYTES) {
        console.info(`[FileUpload] Streaming path chosen (size=${(blob.size / 1024 / 1024).toFixed(1)}mb)`);
        return uploadBlobViaStreamingEndpoint({ blob, s3Key, contentType, authToken, onProgress });
    }
    console.info(`[FileUpload] Base64 path chosen (size=${(blob.size / 1024 / 1024).toFixed(1)}mb)`);

    if (blob.size > DIRECT_UPLOAD_FALLBACK_MAX_BYTES) {
        throw makeUploadError('Backend upload fallback is limited to 25 MB; retry when MinIO is reachable.', {
            status: 413,
            code: 'file_upload_direct_fallback_too_large',
        });
    }

    if (onProgress) onProgress(55);
    const dataBase64 = await blobToBase64(blob);
    if (onProgress) onProgress(70);

    // Writes the bytes under a key the presign already reserved, so a retry
    // after a lost reply overwrites the same object. Never timed (file bytes).
    const requestDirectUpload = (token) => sendWithNetworkRetry(
        (attemptSignal) => fetch(`${API_BASE}/api/files/direct-upload`, {
            method: 'POST',
            headers: _authHeaders(token),
            body: JSON.stringify({
                s3Key,
                contentType,
                sizeBytes: blob.size,
                dataBase64,
            }),
            signal: attemptSignal,
        }),
        { label: 'Backend upload fallback', timeoutMs: UPLOAD_BYTES_TIMEOUT_MS },
    );

    let directResponse = await requestDirectUpload(authToken);
    ({ response: directResponse, authToken } = await retryRequestWithFreshAuth(directResponse, authToken, requestDirectUpload));

    if (!directResponse.ok) {
        const body = await parseJsonSafe(directResponse);
        const message = body.error || body.message || `Backend upload fallback failed (${directResponse.status})`;
        if (directResponse.status === 401) {
            markUploadAuthFailure(authToken);
            throw makeAuthUnavailableError(message);
        }
        if (directResponse.status === 403) {
            throw makeUploadError(message, { status: 403, code: body.code || 'file_upload_forbidden', body });
        }
        if (directResponse.status === 429) {
            const cooldownMs = resolveUploadRetryAfterMs(directResponse, body);
            setUploadCooldown(cooldownMs);
            logUploadCooldownOnce(cooldownMs);
            throw makeCooldownError(cooldownMs);
        }
        throw makeUploadError(message, { status: directResponse.status, body });
    }

    const body = await directResponse.json();
    if (onProgress) onProgress(100);
    return {
        s3Key,
        size: blob.size,
        fileMetadataId: body?.id || null,
        contentType: body?.content_type || contentType,
    };
}

// Inject JWT Bearer token when backend auth is active.
function _authHeaders(token = readAccessToken()) {
    const headers = { 'Content-Type': 'application/json' };
    const safeToken = normaliseAccessToken(token);
    if (safeToken) headers['Authorization'] = `Bearer ${safeToken}`;
    return headers;
}

// ─── Upload ──────────────────────────────────────────────────────────────────

/**
 * Upload a file to S3 via presigned URL (three-step flow).
 *
 * @param {Object} opts
 * @param {Blob|ArrayBuffer|string} opts.data - File content (Blob, ArrayBuffer, or base64 data URL)
 * @param {string} opts.projectId - Parent project ID
 * @param {string} [opts.intakeJobId] - Parent intake job for intake photo derivatives
 * @param {string} opts.entityType - 'project_photo', 'project_document', 'project_video_frame', etc.
 * @param {string} opts.entityId - The file's ID within the parent entity
 * @param {string} opts.contentType - MIME type (e.g., 'image/jpeg')
 * @param {string} [opts.originalFilename] - User-facing filename
 * @param {Object} [opts.cloudMedia] - Required derivative receipt for customer photo/video objects
 * @param {Function} [opts.onProgress] - Progress callback: onProgress(percent)
 * @param {AbortSignal} [opts.signal] - Cancels queued/in-flight trusted image work when its project run is superseded
 * @returns {Promise<{ s3Key: string, size: number, fileMetadataId: string|null, contentType: string }>}
 */
export async function uploadFileToS3({
    data,
    projectId,
    intakeJobId,
    /**
     * migration 114. 'visit' (default) = captured on the site visit; those
     * bytes are only accepted while the job sits in a visit state. 'reference'
     * = material that arrived with the quote request, which the office may
     * attach in any state until the job becomes a project.
     */
    intakeLane = 'visit',
    entityType,
    entityId,
    contentType,
    originalFilename,
    cloudMedia,
    onProgress,
    signal,
}) {
    throwIfUploadAborted(signal);
    const key = uploadRequestKey({ projectId, intakeJobId, intakeLane, entityType, entityId, contentType, originalFilename, cloudMedia });
    const existingEntry = getInFlightUpload(key, data);
    if (existingEntry) {
        return joinInFlightUpload(existingEntry, signal);
    }

    const controller = new AbortController();
    const entry = {
        key,
        payload: data,
        controller,
        waiterCount: 0,
        settled: false,
        promise: null,
    };
    entry.promise = performUploadFileToS3({
        data,
        projectId,
        intakeJobId,
        intakeLane,
        entityType,
        entityId,
        contentType,
        originalFilename,
        cloudMedia,
        onProgress,
        signal: controller.signal,
    }).finally(() => {
        entry.settled = true;
        deleteInFlightUpload(entry);
    });

    setInFlightUpload(entry);
    return joinInFlightUpload(entry, signal);
}

async function performUploadFileToS3({
    data,
    projectId,
    intakeJobId,
    intakeLane = 'visit',
    entityType,
    entityId,
    contentType,
    originalFilename,
    cloudMedia,
    onProgress,
    signal,
}) {
    // Resolve the exact bytes before asking the backend for an upload slot.
    // This lets both sides bind the derivative receipt to the real object size
    // and prevents a caller from swapping source-original bytes after policy
    // approval.
    let blob;
    if (typeof data === 'string' && data.startsWith('data:')) {
        blob = await dataUrlToBlob(data);
    } else if (data instanceof ArrayBuffer) {
        blob = new Blob([data], { type: contentType });
    } else if (data instanceof Blob) {
        blob = data;
    } else {
        throw new Error('uploadFileToS3: data must be Blob, ArrayBuffer, or base64 data URL');
    }
    blob = await repairSingleMissingPdfEofByte(blob, contentType, entityType);
    assertCloudMediaUploadAllowed({ entityType, contentType, cloudMedia, blob });

    let authToken = await ensureUploadAuthSession();
    if (SERVER_NORMALISED_IMAGE_LANES.has(entityType)) {
        return uploadTrustedImageDerivative({
            blob,
            projectId,
            intakeJobId,
            intakeLane,
            entityType,
            entityId,
            originalFilename,
            authToken,
            onProgress,
            signal,
        });
    }
    await waitForUploadUrlBudget();
    // 1. Request presigned PUT URL
    const requestUploadUrl = (token) => sendWithNetworkRetry(
        (attemptSignal) => fetch(`${API_BASE}/api/files/upload-url`, {
            method: 'POST',
            headers: _authHeaders(token),
            body: JSON.stringify({
                projectId,
                ...(intakeJobId ? { intakeJobId } : {}),
                // Omitted for every pre-114 caller; the backend reads its absence
                // as the visit lane, which is what those callers meant.
                ...(intakeJobId && intakeLane !== 'visit' ? { intakeLane } : {}),
                entityType,
                entityId,
                contentType,
                originalFilename,
                sizeBytes: blob.size,
                ...(cloudMedia ? { cloudMedia } : {}),
            }),
            signal: attemptSignal,
        }),
        { label: 'Upload URL request', timeoutMs: UPLOAD_CONTROL_TIMEOUT_MS },
    );
    let urlResponse = await requestUploadUrl(authToken);
    ({ response: urlResponse, authToken } = await retryRequestWithFreshAuth(urlResponse, authToken, requestUploadUrl));

    if (!urlResponse.ok) {
        const body = await parseJsonSafe(urlResponse);
        const message = body.error || body.message || `Upload URL request failed (${urlResponse.status})`;
        if (urlResponse.status === 401) {
            markUploadAuthFailure(authToken);
            throw makeAuthUnavailableError(message);
        }
        if (urlResponse.status === 403) {
            throw makeUploadError(message, { status: 403, code: body.code || 'file_upload_forbidden', body });
        }
        if (urlResponse.status === 429) {
            const cooldownMs = resolveUploadRetryAfterMs(urlResponse, body);
            setUploadCooldown(cooldownMs);
            logUploadCooldownOnce(cooldownMs);
            throw makeCooldownError(cooldownMs);
        }
        // The server's refusal code (document_mime_not_permitted, ...) names
        // what it refused. Without it the job could only say "registration
        // failed" (2026-10-04 UAT F2).
        throw makeUploadError(message, { status: urlResponse.status, code: body.code || undefined, body });
    }

    const { uploadUrl, uploadMode, s3Key } = await urlResponse.json();

    // Protected customer derivatives are backend-only. MinIO presigned PUTs
    // cannot bind MIME/size, so a new backend never issues one for these lanes.
    // Keep accepting the legacy response shape for documents and mixed-version
    // upgrades; an explicit backend-only mode always wins over local flags.
    if (uploadMode === 'backend-only' || !uploadUrl) {
        return uploadBlobViaBackendFallback({ blob, s3Key, contentType, authToken, onProgress });
    }

    // 3. Upload directly to S3 via presigned URL. If the local browser cannot
    // reach the host-published MinIO port, fall back through the authenticated
    // backend once and pause direct attempts briefly to avoid console floods.
    //
    // Council #4 fix (v5.19.60): explicit operator opt-out for environments
    // where direct MinIO :9010 is permanently unreachable (firewall,
    // installer-time port-mapping drift, locked-down LAN). When the operator
    // sets `localStorage.qm_force_backend_upload = 'true'`, every upload
    // skips the direct-PUT attempt and goes straight to the backend
    // streaming endpoint. Eliminates the noisy "Direct MinIO upload
    // unavailable" console.info on every upload + saves one network probe
    // per file. Documented in installer/INSTALL.html for Customer-Zero-style
    // deployments. Streaming (>25 MB) is a separate workstream (v5.19.61+).
    if (_forceBackendUploadFlag() || directS3UploadPaused()) {
        return uploadBlobViaBackendFallback({ blob, s3Key, contentType, authToken, onProgress });
    }

    // Never timed (whole-file bytes). A 502/503/504 from the object store's
    // edge is retried before the backend fallback takes over; a dropped
    // connection is NOT - on the devices that hit it the host-published port
    // is permanently unreachable (the reason this fallback lane exists), so a
    // retry would only delay the lane switch, and the fallback POST below
    // carries its own network retry.
    let uploadResponse;
    try {
        uploadResponse = await sendWithNetworkRetry(
            (attemptSignal) => fetch(uploadUrl, {
                method: 'PUT',
                headers: { 'Content-Type': contentType },
                body: blob,
                signal: attemptSignal,
            }),
            {
                label: 'S3 upload',
                timeoutMs: UPLOAD_BYTES_TIMEOUT_MS,
                shouldRetry: (err) => !isNetworkFetchFailure(err) && isGatewayUnavailableStatus(err?.status),
            },
        );
    } catch (err) {
        markDirectS3UploadUnavailable(err?.status ? `S3 upload failed (${err.status})` : (err?.message || 'network error'));
        return uploadBlobViaBackendFallback({ blob, s3Key, contentType, authToken, onProgress });
    }

    if (!uploadResponse.ok) {
        const status = uploadResponse.status || 0;
        if (status === 0 || status === 403 || status >= 500) {
            if (status === 0 || status >= 500) {
                markDirectS3UploadUnavailable(`S3 upload failed (${status || 'network'})`);
            }
            return uploadBlobViaBackendFallback({ blob, s3Key, contentType, authToken, onProgress });
        }
        throw new Error(`S3 upload failed (${uploadResponse.status})`);
    }

    if (onProgress) onProgress(80);

    // 4. Confirm upload with backend. Idempotent on the server (it stamps
    // confirmed_at on the row the presign reserved), so a lost reply is safe
    // to retry - and without the receipt every preview route rejects the key.
    const requestConfirm = (token) => sendWithNetworkRetry(
        (attemptSignal) => fetch(`${API_BASE}/api/files/confirm`, {
            method: 'POST',
            headers: _authHeaders(token),
            body: JSON.stringify({ s3Key, sizeBytes: blob.size }),
            signal: attemptSignal,
        }),
        { label: 'Upload confirmation', timeoutMs: UPLOAD_CONTROL_TIMEOUT_MS },
    );
    let confirmResponse = await requestConfirm(authToken);
    ({ response: confirmResponse, authToken } = await retryRequestWithFreshAuth(confirmResponse, authToken, requestConfirm));

    // WO-E-001 close: parse the confirm body so callers can stamp
    // `fileMetadataId` onto their in-memory records. The confirm endpoint
    // returns { id, s3_key, content_type, size_bytes, confirmed_at }; we
    // expose `fileMetadataId` as the canonical preview key so downstream
    // UI (the Preview chip on Stage 1 + Stage 3 document cards) can route
    // straight to /api/files/:id/preview without an extra lookup round-trip.
    let fileMetadataId = null;
    let confirmedContentType = contentType;
    if (confirmResponse.ok) {
        try {
            const body = await confirmResponse.json();
            if (body && typeof body === 'object') {
                fileMetadataId = body.id || null;
                confirmedContentType = body.content_type || contentType;
            }
        } catch {
            // Body not JSON — harmless; caller falls back to s3Key-keyed preview.
        }
    } else {
        const body = await parseJsonSafe(confirmResponse);
        if (confirmResponse.status === 401) {
            markUploadAuthFailure(authToken);
            throw makeAuthUnavailableError(body.error || body.message || `Upload confirmation failed (${confirmResponse.status})`);
        }
        if (confirmResponse.status === 403) {
            throw makeUploadError(body.error || body.message || `Upload confirmation failed (403)`, {
                status: 403,
                code: body.code || 'file_upload_forbidden',
                body,
                confirmationFailed: true,
            });
        }
        if (confirmResponse.status === 429) {
            const cooldownMs = resolveUploadRetryAfterMs(confirmResponse, body);
            setUploadCooldown(cooldownMs);
            logUploadCooldownOnce(cooldownMs);
            throw makeCooldownError(cooldownMs);
        }
        // A successful PUT is not a durable customer-evidence receipt. Until
        // /confirm stamps confirmed_at, every preview/download route rejects the
        // key. Propagate the failure so callers retain their source bytes and do
        // not persist an unreachable s3Key as if the upload had completed.
        throw makeUploadError(
            body.error || body.message || `Upload confirmation failed (${confirmResponse.status})`,
            {
                status: confirmResponse.status,
                code: body.code || 'file_upload_confirmation_failed',
                confirmationFailed: true,
            },
        );
    }

    if (onProgress) onProgress(100);

    return { s3Key, size: blob.size, fileMetadataId, contentType: confirmedContentType };
}

/**
 * Upload a batch of files (e.g., all photos in a project).
 * Uploads sequentially to avoid overwhelming the connection.
 *
 * @param {Array} files - Array of { data, entityType, entityId, contentType, originalFilename }
 * @param {string} projectId
 * @param {Function} [onFileProgress] - onFileProgress(fileIndex, totalFiles, percent)
 * @returns {Array<{ entityId, s3Key, size }>}
 */
export async function uploadBatchToS3(files, projectId, onFileProgress) {
    const results = [];

    for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
            const result = await uploadFileToS3({
                ...file,
                projectId,
                onProgress: (pct) => onFileProgress?.(i, files.length, pct),
            });
            results.push({ entityId: file.entityId, ...result });
        } catch (err) {
            console.error(`[FileUpload] Failed to upload ${file.entityId}:`, err.message);
            results.push({ entityId: file.entityId, error: err.message });
        }
    }

    return results;
}

/**
 * Bounded Project-open reconciliation. Multiple cockpit surfaces can mount
 * for the same Project, so calls single-flight and then cool down for five
 * minutes. An explicit retry may bypass that cooldown, but is still capped to
 * the backend route's 5/minute budget. A browser reload naturally receives a
 * fresh session-local budget and retries.
 */
export function reconcileProjectReferenceHealth(
    projectId,
    { force = false, nowMs = Date.now() } = {},
) {
    const id = typeof projectId === 'string' ? projectId.trim() : '';
    if (!id || id.length > 200) return Promise.resolve({ skipped: 'invalid_project_id' });

    // A long-lived operator tab may open hundreds of Projects. Cooldown state
    // only has meaning for the last five minutes, so retire older identities
    // before reading or adding another one rather than retaining every Project
    // seen for the lifetime of the tab.
    pruneProjectReferenceCooldowns(nowMs);

    const existing = projectReferenceReconciliations.get(id);
    if (existing) return existing;

    const lastStartedAt = projectReferenceLastStartedAt.get(id);
    const minInterval = force
        ? PROJECT_REFERENCE_FORCE_MIN_INTERVAL_MS
        : PROJECT_REFERENCE_RECONCILE_COOLDOWN_MS;
    if (Number.isFinite(lastStartedAt) && nowMs - lastStartedAt < minInterval) {
        return Promise.resolve({ skipped: 'cooldown' });
    }

    const token = readAccessToken();
    if (!token || isJwtExpiredOrInvalid(token)) {
        return Promise.resolve({ skipped: 'unauthenticated' });
    }

    while (projectReferenceGlobalStarts.length > 0
        && nowMs - projectReferenceGlobalStarts[0] >= PROJECT_REFERENCE_GLOBAL_WINDOW_MS) {
        projectReferenceGlobalStarts.shift();
    }
    if (projectReferenceGlobalStarts.length >= PROJECT_REFERENCE_GLOBAL_MAX) {
        return Promise.resolve({ skipped: 'global_cooldown' });
    }

    projectReferenceLastStartedAt.set(id, nowMs);
    projectReferenceGlobalStarts.push(nowMs);
    const run = (async () => {
        try {
            const reconcile = await fetch(`${API_BASE}/api/files/reconcile-reference-health`, {
                method: 'POST',
                headers: _authHeaders(token),
                body: JSON.stringify({ projectId: id }),
            });
            if (!reconcile.ok) {
                return { ok: false, status: reconcile.status || 0 };
            }
            const summary = await reconcile.json();

            // The manifest projects durable missing-metadata rows that have no
            // file_metadata record. Ingest it immediately so photo/document
            // surfaces can short-circuit before they each issue a doomed URL
            // request.
            const manifest = await fetch(`${API_BASE}/api/files/project/${encodeURIComponent(id)}`, {
                headers: _authHeaders(token),
            });
            if (manifest.ok) {
                ingestProjectFileHealthManifest(await manifest.json());
            }
            return summary;
        } catch {
            // Project open is never blocked by an optional health refresh.
            // The bounded cooldown prevents a transport outage from becoming
            // a render-driven request storm; a browser reload retries.
            return { ok: false, status: 0 };
        }
    })();
    projectReferenceReconciliations.set(id, run);
    run.finally(() => {
        if (projectReferenceReconciliations.get(id) === run) {
            projectReferenceReconciliations.delete(id);
        }
    });
    return run;
}

// ─── Download URLs ───────────────────────────────────────────────────────────

// Negative cache for keys the backend reported MISSING (object gone from
// storage — the orphaned-photo population, 1,579/1,615 photo objects on the
// Customer Zero corpus). Without it, every project open re-asks the backend
// for every dead key and re-warns per render: dozens of doomed requests and a
// console full of identical 404s (operator report 2026-08-14). Entries expire
// so a later rescue/re-upload of the same key surfaces within minutes without
// a reload. Consumers can detect the cached case via the '(404, cached)'
// message and skip re-logging.
function isKnownMissing(s3Key) {
    return Boolean(fileReferenceUnavailableReason(s3Key));
}

/**
 * Mark a key as missing from OUTSIDE this module. Second dead-photo class
 * (2026-08-14): when the metadata row still exists but the object is gone,
 * the backend happily issues a proxy/signed URL and the miss only surfaces
 * as a 404/410 when that URL is fetched — a path this module never sees.
 * Consumers that fetch signed URLs call this on a gone-class status so the
 * key joins the same negative cache (and drop any cached positive URL, which
 * would otherwise keep resurrecting the doomed fetch for up to 58 minutes).
 *
 * @param {string} s3Key
 */
export function markFileKeyMissing(s3Key) {
    if (!s3Key) return;
    markFileReferenceUnavailable(s3Key, 'cloud_object_missing');
    urlCache.delete(s3Key);
}

/**
 * Un-mark a key after a successful rescue upload so thumbnails re-fetch
 * immediately instead of waiting out the negative-cache TTL.
 *
 * @param {string} s3Key
 */
export function clearFileKeyMissing(s3Key) {
    if (!s3Key) return;
    clearFileReferenceUnavailable(s3Key);
}

/**
 * Drop the cached signed URL for one key, so the next lookup asks for a fresh
 * one. For an <img> that failed to load its URL: retrying with the cached URL
 * would load the same failing address again. Does not mark the key missing.
 *
 * @param {string} s3Key
 */
export function forgetFileUrl(s3Key) {
    if (!s3Key) return;
    urlCache.delete(s3Key);
}

export function getFileReferenceUnavailableReason(reference) {
    return fileReferenceUnavailableReason(reference);
}

// Cloud repair (2026-08-14, D8 durability): the local sync-folder recovery
// restores lost photo BYTES to this device only — every other device still
// 404s, and when the appliance retires (D8) the folder copy is the last one.
// This pushes recovered bytes back to the photo's EXISTING s3Key through the
// dedicated /repair-protected route. The backend accepts only an already-
// confirmed row and exact bytes matching its server-issued size, MIME and
// SHA-256 receipt; ordinary upload attempts cannot use this path.
const RESCUE_MAX_BYTES = 24 * 1024 * 1024; // direct-upload decodes at most 25MB; leave headroom

/**
 * Re-upload recovered bytes for an existing s3Key. Returns true on success,
 * false on any failure (the local recovery already succeeded — cloud repair
 * is best-effort and retries naturally on the next load).
 *
 * @param {string} s3Key
 * @param {Blob} blob
 * @param {Object} cloudMedia - Existing photo derivative receipt
 * @returns {Promise<boolean>}
 */
export async function rescueUploadBlob(s3Key, blob, cloudMedia) {
    if (!s3Key || !(blob instanceof Blob)) return false;
    if (blob.size === 0 || blob.size > RESCUE_MAX_BYTES) return false;
    const decision = decideCloudMediaUpload(cloudMedia, CLOUD_MEDIA_KINDS.PHOTO_PREVIEW);
    if (!decision.allowed) return false;
    if (cloudMedia.derivativeBytes !== blob.size) return false;
    if (cloudMedia.derivativeContentType !== String(blob.type || '').trim().toLowerCase()) return false;
    try {
        // arrayBuffer + chunked btoa, not FileReader: works in every runtime
        // the app touches (browser, worker, node tests) and skips data-URL
        // prefix parsing. 32KB chunks keep String.fromCharCode off the
        // argument-count cliff.
        const bytes = new Uint8Array(await blob.arrayBuffer());
        let binary = '';
        for (let i = 0; i < bytes.length; i += 32768) {
            binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 32768));
        }
        const dataBase64 = (typeof btoa === 'function')
            ? btoa(binary)
            : Buffer.from(bytes).toString('base64');
        if (!dataBase64) return false;
        const response = await fetch(`${API_BASE}/api/files/repair-protected`, {
            method: 'POST',
            headers: _authHeaders(),
            body: JSON.stringify({
                s3Key,
                contentType: blob.type || 'application/octet-stream',
                sizeBytes: blob.size,
                dataBase64,
            }),
        });
        if (!response.ok) return false;
        clearFileKeyMissing(s3Key);
        return true;
    } catch {
        return false;
    }
}

/**
 * Get a presigned download URL for a file. Uses cache to avoid redundant requests.
 *
 * @param {string} s3Key
 * @returns {string} Presigned GET URL
 */
export async function getFileUrl(s3Key) {
    if (!s3Key) return null;

    // Check cache first
    const cached = getCachedUrl(s3Key);
    if (cached) return cached;
    if (isKnownMissing(s3Key)) {
        throw new Error('Failed to get file URL (404, cached)');
    }

    // Send S3 keys in JSON instead of the path. Slash-heavy keys such as
    // tenant/projects/... can be rejected by nginx/Fastify routing even when
    // every slash is percent-encoded.
    const response = await fetch(`${API_BASE}/api/files/batch-urls`, {
        method: 'POST',
        headers: _authHeaders(),
        body: JSON.stringify({ s3Keys: [s3Key] }),
    });
    if (!response.ok) {
        // Transport/server errors are NOT negative-cached: the object may be
        // fine and the backend momentarily unhappy. Only a definitive
        // missing-from-storage answer (200 with the key absent) is cached.
        throw new Error(`Failed to get file URL (${response.status})`);
    }

    const { urls = {} } = await response.json();
    const signedUrl = urls[s3Key];
    if (!signedUrl) {
        markFileKeyMissing(s3Key);
        throw new Error('Failed to get file URL (404)');
    }

    setCachedUrl(s3Key, signedUrl);
    return signedUrl;
}

/**
 * Batch get presigned download URLs (e.g., for loading a project with many photos).
 *
 * @param {string[]} s3Keys
 * @returns {Object} Map of s3Key → signedUrl
 */
export async function getBatchFileUrls(s3Keys) {
    if (!Array.isArray(s3Keys) || s3Keys.length === 0) return {};

    // Split into cached and uncached
    const result = {};
    const uncached = [];

    for (const key of s3Keys) {
        const cached = getCachedUrl(key);
        if (cached) {
            result[key] = cached;
        } else if (!isKnownMissing(key)) {
            uncached.push(key);
        }
        // Known-missing keys are simply omitted from the result, exactly as
        // the backend would omit them — callers already handle absence.
    }

    // Fetch uncached in batch
    if (uncached.length > 0) {
        const response = await fetch(`${API_BASE}/api/files/batch-urls`, {
            method: 'POST',
            headers: _authHeaders(),
            body: JSON.stringify({ s3Keys: uncached }),
        });

        if (response.ok) {
            const { urls } = await response.json();
            for (const [key, url] of Object.entries(urls)) {
                setCachedUrl(key, url);
                result[key] = url;
            }
            // Keys the backend omitted are missing from storage: negative-cache
            // them so the next project open doesn't re-ask per key.
            for (const key of uncached) {
                if (!(key in result)) markFileKeyMissing(key);
            }
        }
    }

    return result;
}

/**
 * Convert any displayable file URL to one the browser can reach from ANY device.
 *
 * Raw MinIO URLs (host-published :9010 / bucket path — see utils/minio-url.js)
 * are rewritten to a same-origin proxy URL via the s3 key they embed; every
 * other URL (data:, blob:, /api/files/ proxy URLs, non-MinIO http) passes
 * through unchanged.
 *
 * Cross-device hardening (2026-06-05): stored project scopes can carry raw
 * :9010 photo URLs that fail with ERR_CONNECTION_REFUSED off the host box.
 * Fail closed for raw MinIO URLs. Returning the original host-published URL
 * would hand an unreachable/private address to `<img>` or `fetch` and can
 * leak a prior machine's storage topology into another device. Callers may
 * continue to an independent blob, metadata, thumbnail, or session fallback.
 *
 * @param {string} url
 * @returns {Promise<string|null>}
 */
export async function toSameOriginDisplayUrl(url) {
    const key = s3KeyFromMinioUrl(url);
    if (!key) return url;
    try {
        const proxied = await getFileUrl(key);
        return proxied && !isRawMinioUrl(proxied) ? proxied : null;
    } catch {
        return null;
    }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Convert a base64 data URL to a Blob.
 * Re-export from shared utility - NEVER use fetch() on data URLs (CSP violation).
 */
export { dataUrlToBlob };

/**
 * Clear the URL cache. Useful for testing or tenant switch.
 */
export function clearUrlCache() {
    urlCache.clear();
}

/**
 * Read-only session telemetry for the admin health surface and soak tests.
 * Values are counts only: no customer keys, filenames, URLs or bytes escape.
 */
export function getFileUploadSessionResourceCounts() {
    return {
        signedUrlCacheEntries: urlCache.size,
        inFlightUploadKeys: inFlightUploads.size,
        projectReferenceFlights: projectReferenceReconciliations.size,
        projectReferenceCooldownEntries: projectReferenceLastStartedAt.size,
        projectReferenceGlobalStarts: projectReferenceGlobalStarts.length,
        trustedImageWindowStarts: trustedImageRequestStarts.length,
    };
}

export function __resetFileUploadRateStateForTests() {
    uploadUrlGate = Promise.resolve();
    lastUploadUrlRequestAt = 0;
    uploadUrlCooldownUntil = 0;
    lastUploadCooldownLogAt = 0;
    uploadAuthCooldownUntil = 0;
    uploadAuthCooldownToken = null;
    trustedImageUploadGate = Promise.resolve();
    trustedImageRetryNotBefore = 0;
    lastTrustedImageCooldownLogAt = 0;
    trustedImageRequestStarts.length = 0;
    for (const payloads of inFlightUploads.values()) {
        for (const entry of payloads.values()) {
            entry.controller?.abort?.('file upload test state reset');
        }
    }
    inFlightUploads.clear();
    projectReferenceReconciliations.clear();
    projectReferenceLastStartedAt.clear();
    projectReferenceGlobalStarts.length = 0;
    urlCache.clear();
    __resetFileReferenceHealthForTests();
}
